import { type Document, DOMParser, MIME_TYPE, type Element } from '@xmldom/xmldom'
import { getStringXmlAttributes, xmlNodeIsElement } from './XmlParsing'
import { err, ok, Result } from 'neverthrow'
import { serializeFusionError, type FusionError } from '../error/FusionError'
import { xflowBlobDecompress } from './XflowBlob'
import { ZXflowConceptsByCid, type XflowConceptsByCid } from './XflowConcept'
import { safeParseJson } from '../safeparse/safeParseJson'
import { BranchDesignator } from '../BranchDesignator'

export const ERR_FLOWMAP_ROOT_NODE_NOT_FOUND = 'root node not found'
export const ERR_FLOWMAP_UNNAMED_BLOB_NODE = 'unnamed blob node'
export const ERR_FLOWMAP_EMPTY_BLOB = (blobName: string) => `blob '${blobName}' is empty`
export const ERR_FLOWMAP_DUPLICATE_BLOB = (blobName: string) => `blob name '${blobName}' is duplicated`
export const ERR_FLOWMAP_CONCEPTS_BLOB_NOT_FOUND = 'concepts blob not found'

const isRootNode = (node: Element) => (node.nodeName === 'XFlow')

export const flowmapBranchDesignator = (flowMap: string): BranchDesignator|null => {
	let doc: Document
	try {
		doc = new DOMParser().parseFromString(flowMap, MIME_TYPE.XML_APPLICATION)
	} catch {
		return null
	}
	for(let node of doc.childNodes) {
		if(!xmlNodeIsElement(node) || !isRootNode(node)) {
			continue
		}
		switch(node.getAttribute('branchDesignator')) {
			case BranchDesignator.InHome:
				return BranchDesignator.InHome
			case BranchDesignator.VideoVisit:
				return BranchDesignator.VideoVisit
		}
		return null
	}
	return null
}

// the frontend needs to be able to read the concepts out of any historic
// flowmap in order to understand which file blobs to download
// during a sync, so we have located this implementation to
// the backcompat module
export class FlowmapParser {
	doc: Document
	rootNode: Element
	namedBlobs = new Map<string, string>()
	pageNodes: Element[] = []
	concepts: XflowConceptsByCid = {}

	private constructor(doc: Document, rootNode: Element) {
		this.doc = doc
		this.rootNode = rootNode
	}

	static create(flowMap: string): Result<FlowmapParser, FusionError> {
		let parser: DOMParser
		let doc: Document
		try {
			parser = new DOMParser()
			doc = parser.parseFromString(flowMap, MIME_TYPE.XML_APPLICATION)
		} catch(ex) {
			return err(serializeFusionError('FlowmapParser', ex))
		}
		let rootNode: Element|null = null
		for(let node of doc.childNodes) {
			if(xmlNodeIsElement(node) && isRootNode(node)) {
				rootNode = node
				break
			}
		}
		if(rootNode === null) {
			return err(serializeFusionError('FlowmapParser', ERR_FLOWMAP_ROOT_NODE_NOT_FOUND))
		}
		const instance = new FlowmapParser(doc, rootNode)
		const rParseNodes = instance.parseNodes()
		if(rParseNodes.isErr()) {
			return err(rParseNodes.error)
		}
		const rParseConcepts = instance.parseConcepts()
		if(rParseConcepts.isErr()) {
			return err(rParseConcepts.error)
		}
		return ok(instance)
	}

	private parseNodes(): Result<void, FusionError> {
		if(!this.rootNode.hasChildNodes()) {
			// being this aggressive ruins simple tests, and quite honestly
			// it will be obvious if the concepts aren't present
			// return err(serializeFusionError('FlowmapParser parseNodes', ERR_FLOWMAP_CONCEPTS_BLOB_NOT_FOUND))
			return ok()
		}
		for(let node of this.rootNode.childNodes) {
			if(!xmlNodeIsElement(node)) {
				continue
			}
			switch(node.nodeName) {
				case 'Blob':
					let blobAttr = getStringXmlAttributes(node)
					let blobName = blobAttr["name"]
					if(!blobName) {
						return err(serializeFusionError('FlowmapParser', ERR_FLOWMAP_UNNAMED_BLOB_NODE))
					}
					let blobData = node.firstChild?.textContent
					if(!blobData) {
						return err(serializeFusionError('FlowmapParser', ERR_FLOWMAP_EMPTY_BLOB(blobName)))
					}
					let rDecomp = xflowBlobDecompress(blobData)
					if(rDecomp.isErr()) {
						return err(serializeFusionError('FlowmapParser', rDecomp.error))
					}
					if(this.namedBlobs.has(blobName)) {
						return err(serializeFusionError('FlowmapParser', ERR_FLOWMAP_DUPLICATE_BLOB(blobName)))
					}
					this.namedBlobs.set(blobName, rDecomp.value)
					break
				case 'Page':
					this.pageNodes.push(node)
					break
			}
		}
		return ok()
	}

	private parseConcepts(): Result<void, FusionError> {
		const jConcepts = this.namedBlobs.get('concepts')
		if(!jConcepts) {
			// being this aggressive ruins simple tests, and quite honestly
			// it will be obvious if the concepts aren't present
			// return err(serializeFusionError('FlowmapParser getConcepts', ERR_FLOWMAP_CONCEPTS_BLOB_NOT_FOUND))
			return ok()
		}
		const rParse = safeParseJson(jConcepts, ZXflowConceptsByCid)
		if(rParse.isErr()) {
			return err(serializeFusionError('FlowmapParser getConcepts safeParse', rParse.error))
		}
		this.concepts = rParse.value
		return ok()
	}
}
